import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import RotatingVerb from "@/components/RotatingVerb";

import RosterScatter from "@/components/mcp/RosterScatter";
import { Boxes as HeroBoxes } from "@/components/HeroBoxes";
import HeroVideoBG from "@/components/HeroVideoBG";
import HeroAgentChat from "@/components/HeroAgentChat";


// Lives in /public so index.html can <link rel="preload"> it without a hash.
const isMobileViewport = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(max-width: 767px)").matches || window.innerWidth < 768);

gsap.registerPlugin(ScrollTrigger);

const importLionAgentPopup = () => import("@/components/LionAgentPopup");
const LionAgentPopup = lazy(importLionAgentPopup);

// WovenLightCanvas + HeroMeshGradient (three.js + @paper-design/shaders-react)
// were dead-imported here — never rendered in any JSX. Removing them drops
// the 439 KB `three` chunk from the build entirely. If we ever bring back a
// three.js canvas, restore the lazy import + the useEnableHeavyCanvas idle
// gate that used to live here (git history at commit before this one).

const RotatingWord: React.FC<{ words: string[]; intervalMs?: number }> = ({
  words,
  intervalMs = 2600,
}) => {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % words.length), intervalMs);
    return () => clearInterval(id);
  }, [words.length, intervalMs]);
  // Reserve width of the longest word so layout doesn't shift.
  const longest = words.reduce((a, b) => (a.length >= b.length ? a : b), "");
  return (
    <span
      className="relative inline-flex align-baseline overflow-hidden"
      style={{ height: "1em", verticalAlign: "baseline" }}
    >
      <span aria-hidden className="invisible whitespace-nowrap">{longest}</span>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={words[i]}
          initial={{ y: "0.9em", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-0.9em", opacity: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="absolute left-0 top-0 whitespace-nowrap italic"
          style={{ fontFamily: '"Instrument Serif", serif', fontWeight: 400 }}
        >
          {words[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};


const ROTATING_WORDS: { lead: string; accent: string }[] = [
  { lead: "Browses the web", accent: "for you." },
  { lead: "Finds and reaches", accent: "new leads." },
  { lead: "Codes in any", accent: "environment." },
  { lead: "Builds your", accent: "pitch decks." },
  { lead: "Designs your", accent: "brand." },
  { lead: "Writes and ships", accent: "your blog." },
  { lead: "Answers your", accent: "customers." },
  { lead: "Analyzes your", accent: "data." },
  { lead: "Runs your", accent: "outbound." },
  { lead: "Books your", accent: "meetings." },
  { lead: "Drafts your", accent: "proposals." },
  { lead: "Reviews your", accent: "pull requests." },
  { lead: "Ships your", accent: "product." },
  { lead: "Ranks you", accent: "on Google." },
  { lead: "Researches", accent: "any company." },
  { lead: "Briefs you", accent: "every morning." },
  { lead: "Keeps your", accent: "CRM clean." },
  { lead: "Remembers", accent: "everything." },
  { lead: "Works while", accent: "you sleep." },
];

const SR_VERBS = [
  "Browses the web",
  "Finds and reaches new leads",
  "Codes in any environment",
  "Builds pitch decks",
  "Designs your brand",
  "Writes and ships your blog",
  "Answers your customers",
  "Analyzes your data",
  "Runs your outbound",
  "Books your meetings",
  "Drafts your proposals",
  "Reviews your pull requests",
  "Ships your product",
  "Ranks you on Google",
  "Researches any company",
  "Briefs you every morning",
  "Keeps your CRM clean",
  "Remembers everything",
  "Works while you sleep",
];

const QUOTES = [
  "Blew me away",
  "Feels magical",
  "Genuinely ahead",
  "Like hiring ten people",
  "Quietly brilliant",
  "Saved me a week",
  "Just works",
  "Felt the future",
  "Couldn't go back",
  "Unreasonably good",
  "Shipped overnight",
  "Best hire I've made",
];

const HEADLINE_VERBS = ["gets sharper", "gets more capable", "improves itself", "iterates", "compounds"];

const HEADLINES = [
  "Your agent orchestrates while you sleep.",
  "Your team of one ships by morning.",
  "Your night shift resolves before you wake.",
  "Your second brain executes in the background.",
  "Your quiet co-worker learns from yesterday.",
  "Your institutional brain ships before tomorrow.",
  "One prompt, refined while you think.",
  "Build like a team that's been there a year.",
  "Memory that compounds with every shipped decision.",
  "Institutional memory the agent actually uses.",
  "Two-person team, ten-person output.",
  "Your quietest agents work the night shift.",
  "An institutional brain that never drops context.",
  "What the brain remembers, the agent ships.",
  "Give your codebase a memory that doesn't fade.",
];

const AUDIENCE_LINES = [
  { lead: "Have a Claude subscription?" },
  { lead: "Have a Codex subscription?" },
  { lead: "Already running an agent?" },
  { lead: "Just getting started with AI agents?" },
];

const Hero = () => {
  const [show, setShow] = useState(false);
  const [wordIdx, setWordIdx] = useState(0);
  const [verbIdx, setVerbIdx] = useState(0);
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [tailIdx, setTailIdx] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [audienceIndex, setAudienceIndex] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setAudienceIndex((n) => (n + 1) % AUDIENCE_LINES.length),
      3200
    );
    return () => window.clearInterval(id);
  }, []);

  const sectionRef = useRef<HTMLElement | null>(null);
  const headlineRef = useRef<HTMLHeadingElement | null>(null);
  const subcopyRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Rotate the headline tail every 5.2s. Respects reduced-motion by holding the first phrase.
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setInterval(() => {
      setTailIdx((i) => (i + 1) % HEADLINES.length);
    }, 5200);
    return () => window.clearInterval(id);
  }, []);

  // Rotate the painterly-panel phrase (ROTATING_WORDS) every 3.2s so it
  // cycles even before the user scrolls into the takeover sequence.
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setInterval(() => {
      setWordIdx((i) => (i + 1) % ROTATING_WORDS.length);
    }, 3200);
    return () => window.clearInterval(id);
  }, []);

  // (Removed) Hero-frames scrub preloader — used by the deleted painterly
  // lower panel. The 60-frame WebP sequence and the dedicated /hero-frames/
  // and /hero-frames-mobile/ directories under /public/ are gone.

  // Cinematic scroll, lion zooms, headline parallaxes, panel drifts.
  // Uses window scroll position (start: 0, end: hero height) so it fires
  // immediately from the very top of the page.
  // Mobile runs the same timeline (panel takeover, phrase scaling,
  // manifesto fade-in) but with frame downloads skipped above, the canvas
  // overlay simply stays transparent because no frame images are loaded,
  // and drawFrame() returns early on missing frames. This preserves the
  // animation feel without the 5 MB of webp scrubbing data.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !sectionRef.current) return;

    // Wait for framer-motion's entrance animations (~0.8s + 0.1s delay) to
    // fully settle before measuring layout and binding ScrollTrigger. Mounting
    // GSAP mid-entrance causes a visible jitter as transforms collide.
    const raf = window.setTimeout(() => requestAnimationFrame(() => {
      const ctx = gsap.context(() => {
        const triggerCfg = {
          trigger: sectionRef.current!,
          start: "top top",
          end: "bottom 20%",
          scrub: 0.8,
        };

        if (headlineRef.current) {
          gsap.to(headlineRef.current, {
            yPercent: -40,
            opacity: 0.4,
            ease: "none",
            scrollTrigger: triggerCfg,
          });
        }

        // Drive the headline-subcopy verb (HEADLINE_VERBS) from scroll
        // progress on the hero section trigger, same range that drives the
        // headline parallax above. Scrolling cycles "gets sharper / gets
        // more capable / improves itself / iterates / compounds" instead of
        // a 3.6 s timer ticking independently of the user.
        let lastSetVerbIdx = -1;
        ScrollTrigger.create({
          trigger: sectionRef.current!,
          start: "top top",
          end: "bottom 20%",
          onUpdate: (self) => {
            const next = Math.min(
              HEADLINE_VERBS.length - 1,
              Math.floor(self.progress * HEADLINE_VERBS.length)
            );
            if (next !== lastSetVerbIdx) {
              lastSetVerbIdx = next;
              setVerbIdx(next);
            }
          },
          onLeave: () => {
            if (lastSetVerbIdx !== HEADLINE_VERBS.length - 1) {
              lastSetVerbIdx = HEADLINE_VERBS.length - 1;
              setVerbIdx(HEADLINE_VERBS.length - 1);
            }
          },
        });


        ScrollTrigger.refresh();
      }, sectionRef.current);

      // Stash for cleanup
      (sectionRef.current as any).__gsapCtx = ctx;

      // One more refresh after images/fonts settle to fix bad initial measurements
      // when the user scrolls before layout completes.
      const refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 400);
      (sectionRef.current as any).__refreshTimer = refreshTimer;
    }), 950);

    return () => {
      clearTimeout(raf);
      const ctx = (sectionRef.current as any)?.__gsapCtx;
      const t = (sectionRef.current as any)?.__refreshTimer;
      if (t) clearTimeout(t);
      if (ctx) ctx.revert();
    };
  }, []);

  // Quote rotator only, the word rotator (panel phrase) is now driven
  // entirely by scroll progress via the ScrollTrigger above, so no timer
  // for it here. Quotes still cycle on a timer because they're displayed
  // separately from the scroll-driven panel phrase. Pause when tab hidden
  // to save battery on mobile.
  useEffect(() => {
    let quoteId: number | undefined;
    const start = () => {
      if (quoteId === undefined)
        quoteId = window.setInterval(
          () => setQuoteIdx((i) => (i + 1) % QUOTES.length),
          4200
        );
    };
    const stop = () => {
      if (quoteId !== undefined) { clearInterval(quoteId); quoteId = undefined; }
    };
    const handle = () => (document.hidden ? stop() : start());
    start();
    document.addEventListener("visibilitychange", handle);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", handle);
    };
  }, []);

  // Allow other components (e.g. LiveAgentOps banner) to open the terminal
  useEffect(() => {
    const open = () => setPopupOpen(true);
    window.addEventListener("mane:open-terminal", open);
    return () => window.removeEventListener("mane:open-terminal", open);
  }, []);

  // Preload the popup chunk during idle time so the first click feels instant
  useEffect(() => {
    if (typeof window === "undefined") return;
    const idle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 600));
    const id = idle(() => { importLionAgentPopup(); });
    return () => {
      const cancel = (window as any).cancelIdleCallback;
      if (cancel && typeof id === "number") cancel(id);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative isolate pt-0 pb-0 overflow-hidden bg-background text-foreground"
    >
      {/* SEO / crawler-only */}
      <span className="sr-only">
        Mane is an autonomous team of AI agents that browses the web, finds and reaches new
        leads, codes in any environment, builds pitch decks, designs your brand, writes and
        ships your blog, answers your customers, analyzes your data, runs your outbound,
        books your meetings, drafts proposals, reviews pull requests, ships your product,
        ranks you on Google, researches any company, briefs you every morning, keeps your
        CRM clean, remembers everything, and works while you sleep.
      </span>
      <h2 className="sr-only">What Mane does for you</h2>
      <ul className="sr-only">
        {SR_VERBS.map((v) => (
          <li key={v}>{v}</li>
        ))}
      </ul>

      {/* Background lion dot animation removed per design, keep video panel only. */}

      {/* (Removed) Decorative HeroBoxes grid — cleaner hero per design. */}

      {/* Subtle amber spotlight wash, anchored to the top headline area so it
          stays visible above the painterly video panel. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[80vh] z-[5] overflow-hidden"
      >
        <div className="hero-spot hero-spot--amber" />
        <div className="hero-spot hero-spot--orange" />
        <div className="hero-spot hero-spot--blue" />
      </div>
      <style>{`
        .hero-spot {
          position: absolute;
          left: 50%;
          top: 30%;
          width: 50vw;
          height: 42vh;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          filter: blur(80px);
          mix-blend-mode: screen;
          will-change: transform, opacity;
        }
        .hero-spot--amber {
          background: radial-gradient(ellipse at center, hsl(var(--accent) / 0.09) 0%, transparent 65%);
          animation: hero-spot-a 11s ease-in-out infinite;
        }
        .hero-spot--orange {
          background: radial-gradient(ellipse at center, hsl(28 95% 60% / 0.05) 0%, transparent 65%);
          animation: hero-spot-c 13s ease-in-out infinite;
        }
        .hero-spot--blue {
          background: radial-gradient(ellipse at center, hsl(210 80% 60% / 0.045) 0%, transparent 65%);
          animation: hero-spot-e 17s ease-in-out infinite;
        }
        @keyframes hero-spot-a {
          0%, 100% { transform: translate(-70%, -60%) scale(1); opacity: 0.9; }
          33%      { transform: translate(-25%, -70%) scale(1.15); opacity: 1; }
          66%      { transform: translate(-75%, -30%) scale(0.9); opacity: 0.7; }
        }
        @keyframes hero-spot-c {
          0%, 100% { transform: translate(-30%, -65%) scale(1.1); opacity: 0.7; }
          50%      { transform: translate(-75%, -35%) scale(0.85); opacity: 1; }
        }
        @keyframes hero-spot-e {
          0%, 100% { transform: translate(-75%, -70%) scale(1.15); opacity: 0.6; }
          50%      { transform: translate(-25%, -30%) scale(0.85); opacity: 0.95; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-spot { animation: none !important; }
        }

        /* Chunky pixelated drop-shadow on the headline.
           Stacked offsets in 2px steps build a stepped, blocky shadow.
           Light grey, subtle, sits behind the type. */
        .hero-headline-pixel {
          text-shadow:
            2px 2px 0 hsl(0 0% 88% / 0.85),
            4px 4px 0 hsl(0 0% 88% / 0.7),
            6px 6px 0 hsl(0 0% 90% / 0.55),
            8px 8px 0 hsl(0 0% 90% / 0.4),
            10px 10px 0 hsl(0 0% 92% / 0.28),
            12px 12px 0 hsl(0 0% 92% / 0.18),
            14px 14px 0 hsl(0 0% 94% / 0.1);
        }
        :root.dark .hero-headline-pixel,
        .dark .hero-headline-pixel {
          text-shadow:
            2px 2px 0 hsl(0 0% 100% / 0.08),
            4px 4px 0 hsl(0 0% 100% / 0.06),
            6px 6px 0 hsl(0 0% 100% / 0.045),
            8px 8px 0 hsl(0 0% 100% / 0.035),
            10px 10px 0 hsl(0 0% 100% / 0.025),
            12px 12px 0 hsl(0 0% 100% / 0.018),
            14px 14px 0 hsl(0 0% 100% / 0.012);
        }
      `}</style>

      {/* ===== Video hero — full-bleed background + chat widget =====
          Layout: on mobile/tablet (<lg), content is taller than 100svh
          on shorter handsets, so we use `items-start` + an explicit
          top-padding that clears the overlay navbar. Without this the
          flex `items-center` was vertically-centering an overflowing
          column and pushing the H1 *into* the navbar zone (visible as
          MANE logo overlapping the headline on mobile screenshots).
          On lg+ we restore items-center because the 2-col layout
          comfortably fits 100svh and centering reads better. */}
      <div className="relative h-[100svh] overflow-hidden md:min-h-[100svh] flex items-start lg:items-center pt-[88px] sm:pt-[96px] lg:pt-[84px]">
        <HeroVideoBG />
        <div
          aria-hidden
          className="absolute inset-0 z-[1]"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.18) 45%, rgba(0,0,0,0.42) 100%)",
          }}
        />
        <div className="relative z-10 w-full max-w-content mx-auto px-6 sm:px-8 py-0 sm:py-14 lg:py-16 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-4 sm:gap-10 lg:gap-16 items-start lg:items-center pointer-events-none overflow-hidden">
          {/* Left column: headline + CTAs.
              Keep opacity at 1 from t=0 so this column is the LCP
              candidate at React mount, not at mount+animation-end.
              The slide-in (y:12→0) is purely cosmetic and doesn't
              gate LCP because the element is fully painted for the
              whole 700 ms of the slide. */}
          <motion.div
            initial={{ opacity: 1, y: 12 }}
            animate={{ opacity: 1, y: show ? 0 : 12 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <h1
              ref={headlineRef}
              className="text-[clamp(26px,7.6vw,46px)] sm:text-[clamp(36px,5.2vw,64px)] font-medium leading-[1.08] sm:leading-[1.02] mb-4 sm:mb-6 max-w-[16ch] sm:max-w-[16ch] tracking-[-0.018em] sm:tracking-[-0.035em] text-white mt-0"
            >
              <span className="whitespace-nowrap">The agent <RotatingWord words={["hire", "system", "teammate", "department", "employee"]} /></span> you'll wonder how you ever worked without.
            </h1>

            <p className="text-[14px] sm:text-base text-white max-w-[36ch] sm:max-w-[480px] mb-5 sm:mb-8 leading-[1.45] sm:leading-[1.5]">
              Runs every function 24/7: research, dev, design, outreach, content, support, legal. Remembers everything. Learns and self-improves.
            </p>

            <div className="grid grid-cols-2 sm:flex gap-3 mb-4 sm:mb-7 pointer-events-auto max-w-full sm:max-w-none">
              <a
                href="sms:+13055092351?&body=Hey%20Mane!%20I'd%20love%20to%20get%20started%20with%20AI%20Agents."
                className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-[10px] rounded-[8px] bg-white text-[#171717] font-sans text-[14px] font-medium leading-none shadow-[0_8px_24px_-10px_rgba(0,0,0,0.4)] hover:-translate-y-px transition-all duration-150 whitespace-nowrap"
              >
                Chat with Mane
              </a>
              <Link
                to="/get-mane"
                className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-[10px] rounded-[8px] text-white font-sans text-[14px] font-medium leading-none shadow-[0_0_0_1px_rgba(255,255,255,0.35)_inset] hover:bg-white/12 transition-all duration-150 whitespace-nowrap"
              >
                Get Mane
              </Link>
            </div>

          </motion.div>

          {/* Right column: Sierra-style floating chat (no panel chrome).
              On mobile it sits directly under the CTAs and bottom-anchors
              so messages stack upward, with the top fading into the page. */}
          <div className="hero-chat-window flex h-[clamp(340px,46svh,420px)] min-h-0 flex-col items-stretch justify-end overflow-hidden pointer-events-auto -mt-1 lg:mt-0 lg:h-[min(560px,72svh)] lg:items-end lg:justify-end lg:overflow-hidden">
            <HeroAgentChat />
          </div>
        </div>
      </div>
      {popupOpen && (
        <Suspense fallback={null}>
          <LionAgentPopup open={popupOpen} onClose={() => setPopupOpen(false)} />
        </Suspense>
      )}
    </section>
  );
};

export default Hero;
